import { useId, useState, type ReactNode } from 'react';

/**
 * The label an ICON-ONLY control needs, for people who can see it.
 *
 * `title` is not that. A native tooltip appears only on hover, only after a delay a
 * page cannot change, never on keyboard focus, and is not read consistently — so a
 * control whose entire meaning is a 16px glyph is unlabelled for anyone who reaches
 * it with Tab. This shows the same words on `hover` AND on `focus-within` (WCAG
 * 1.4.13), and Escape dismisses it without moving focus, which is that criterion's
 * "dismissable" half.
 *
 * ACCESSIBLE NAME vs DESCRIPTION. The tip is `role="tooltip"` and the control points
 * at it with `aria-describedby` — the NAME still comes from the control's own
 * `aria-label`. Naming it here instead would leave the name and the visible text free
 * to drift apart, and a control with no `aria-label` would be silent whenever the tip
 * is not rendered.
 *
 * Usage: `<Tooltip text="…">{(id) => <button aria-label="…" aria-describedby={id} …/>}</Tooltip>`
 * — the child gets the id so the description is wired by the caller, not guessed here.
 */
export function Tooltip({
  text,
  children,
}: {
  text: string;
  children: (describedBy: string) => ReactNode;
}) {
  const id = useId();
  // Escape hides the tip while the pointer/focus stays where it is, so the next
  // Escape reaches the dialog behind it rather than being eaten by a tooltip.
  const [dismissed, setDismissed] = useState(false);

  return (
    <span
      className="tip-host"
      onMouseEnter={() => setDismissed(false)}
      onMouseLeave={() => setDismissed(false)}
      onFocus={() => setDismissed(false)}
      onKeyDown={(e) => {
        if (e.key === 'Escape') setDismissed(true);
      }}
    >
      {children(id)}
      {/* Always in the DOM: `aria-describedby` must resolve even while the tip is
          visually hidden, or the description exists only on hover. */}
      <span id={id} role="tooltip" className="tip" hidden={dismissed}>
        {text}
      </span>
    </span>
  );
}
